import { Component, OnInit } from '@angular/core';
import { ActivatedRoute, Router } from '@angular/router';
import { CommonDropdownData } from 'src/app/components/common/common-dropdown/common-dropdown.component.model';
import { CommonConfirmationModalData } from 'src/app/components/common/common-confirmation-modal/common-confirmation-modal.component.model';
import {
  MigrationProcessing,
  MigrationProcessingNote,
  MigrationExecution,
  MigrationApplicationHistory,
  ScheduledTaskQueueItem,
  Workflow,
  WorkflowMigrationRequest
} from 'src/app/models';
import { forkJoin } from 'rxjs';
import { UploadResponse } from 'src/app/components/upload/upload.component';
import {
  DataService,
  WorkflowContextService,
  WorkflowService
} from 'src/app/services';
import { Utilities } from 'src/app/services/utilities';
import {
  ActivityChange,
  ApplicationDataValidationStatus,
  MigrationSummary,
  buildHierarchicalActivities,
  getClientIdFromRoute,
  getRouteParam,
  normalizeMigrationMetadataValidationResult
} from '../settings-migrate-workflow.models';

@Component({
  selector: 'app-settings-migrate-workflow-detail',
  standalone: false,
  templateUrl: './settings-migrate-workflow-detail.component.html',
  styleUrls: ['./settings-migrate-workflow-detail.component.scss']
})
export class SettingsMigrateWorkflowDetailComponent implements OnInit {
  clientId = '';
  migrationProcessingId = '';
  processing: MigrationProcessing | null = null;
  templateWorkflows: Workflow[] = [];
  availableUsers: any[] = [];

  loadingPage = true;
  working = false;
  reprocessingMetadata = false;
  metadataValidationErrors: string[] = [];
  errorMessage = '';
  successMessage = '';
  applicationDataMigrationError = '';
  workflowTaskStatus:
    | 'Queued'
    | 'Processing'
    | 'Completed'
    | 'Error'
    | null = null;
  applicationCoordinatorTasks: ScheduledTaskQueueItem[] = [];

  activeTab: 'execution' | 'data' | 'monitor' | 'history' = 'execution';
  migrationExecutions: MigrationExecution[] = [];
  migrationApplicationHistories: MigrationApplicationHistory[] = [];
  loadingHistory = false;
  showDataEntitiesGrid = false;
  showMigrationForm = false;
  showChangeOwnerDialog = false;

  selectedWorkflowId = '';
  workflowName = '';
  overwriteWorkflow = false;
  summaryVerified = false;
  migrationSummary: MigrationSummary | null = null;
  hierarchicalActivities: ActivityChange[] = [];
  customListsToUpdate = new Set<string>();
  rolesToUpdate = new Set<string>();

  newOwnerId = '';
  newNote = '';

  applicationDataValidationStatus: ApplicationDataValidationStatus | null = null;
  showValidationDetails = false;

  completeConfirmModal: CommonConfirmationModalData = {
    open: false,
    yesNoBtn: true,
    replaceYesWith: 'MARK COMPLETE',
    replaceNoWith: 'CANCEL',
    modalDescp: 'Mark migration complete?',
    modalSubDescp:
      'This removes the migration from the ready-to-start list and should only be done after workflow and application migration are verified.'
  };

  deleteConfirmModal: CommonConfirmationModalData = {
    open: false,
    yesNoBtn: true,
    replaceYesWith: 'DELETE',
    replaceNoWith: 'CANCEL',
    modalDescp: 'Delete migration record?',
    modalSubDescp:
      'Deleting hides this migration processing record from the queue.'
  };

  queueAnotherApplicationMigrationConfirmModal: CommonConfirmationModalData = {
    open: false,
    yesNoBtn: true,
    replaceYesWith: 'QUEUE ANOTHER MIGRATION',
    replaceNoWith: 'CANCEL',
    modalDescp: 'Application migration is already queued or running',
    modalSubDescp:
      'Another application migration coordinator is queued or executing. Queue an additional coordinator for the current application data file?'
  };

  constructor(
    private route: ActivatedRoute,
    private router: Router,
    private dataService: DataService,
    private workflowService: WorkflowService,
    public context: WorkflowContextService
  ) {}

  ngOnInit(): void {
    this.clientId = getClientIdFromRoute(this.route, this.context) || '';
    this.migrationProcessingId =
      getRouteParam(this.route, 'migrationProcessingId') || '';

    if (!this.clientId || !this.migrationProcessingId) {
      this.errorMessage = 'Unable to load this workflow migration.';
      this.loadingPage = false;
      return;
    }

    this.loadPage();
  }

  get ownerDropdownData(): CommonDropdownData {
    return {
      placeholder: 'Select owner',
      selectedValue: this.newOwnerId || '',
      options: [
        { label: 'Unassigned', value: '' },
        ...this.availableUsers.map(user => ({
          label: user.name,
          value: user.id
        }))
      ]
    };
  }

  get templateWorkflowDropdownData(): CommonDropdownData {
    return {
      placeholder: 'Create New Workflow',
      selectedValue: this.selectedWorkflowId || '',
      options: [
        { label: 'Create New Workflow', value: '' },
        ...this.templateWorkflows.map(workflow => ({
          label: workflow.version?.name || workflow.name,
          value: workflow.id
        }))
      ]
    };
  }

  get canGenerateSummary(): boolean {
    return !!this.processing && !!this.workflowName.trim() && !this.working;
  }

  get canExecuteMigration(): boolean {
    return (
      !!this.processing &&
      !!this.migrationSummary &&
      this.summaryVerified &&
      !this.hasErrors() &&
      !this.isWorkflowMigrationQueued &&
      !this.working
    );
  }

  get canMarkComplete(): boolean {
    return !!(
      this.processing?.workflowMigratedOn &&
      this.processing?.applicationDataFilePath &&
      this.processing?.applicationDataMigratedOn &&
      !this.working
    );
  }

  get canPerformMigration(): boolean {
    return true;
  }

  get hasWorkflowMigration(): boolean {
    return !!this.processing?.workflowMigratedOn;
  }

  get isWorkflowMigrationQueued(): boolean {
    return (
      this.workflowTaskStatus === 'Queued' ||
      this.workflowTaskStatus === 'Processing'
    );
  }

  get activeApplicationCoordinatorTasks(): ScheduledTaskQueueItem[] {
    return this.applicationCoordinatorTasks.filter(task => {
      const status = this.getScheduledTaskStatus(task);
      return status === 'Queued' || status === 'Processing';
    });
  }

  get latestApplicationCoordinatorTask(): ScheduledTaskQueueItem | null {
    return this.getLatestTask(this.applicationCoordinatorTasks);
  }

  get applicationCoordinatorStatus(): string {
    const activeTask = this.getLatestTask(this.activeApplicationCoordinatorTasks);
    if (activeTask) {
      return this.getScheduledTaskStatus(activeTask);
    }

    if (this.latestApplicationCoordinatorTask) {
      return this.getScheduledTaskStatus(this.latestApplicationCoordinatorTask);
    }

    return this.processing?.applicationDataMigratedOn ? 'Completed' : 'Pending';
  }

  get workflowStatusText(): string {
    if (this.processing?.workflowMigratedOn) {
      return 'Migrated';
    }

    return this.workflowTaskStatus || 'Pending';
  }

  get applicationDataStatusText(): string {
    if (this.processing?.applicationDataMigratedOn) {
      return 'Migrated';
    }

    if (this.processing?.applicationDataFilePath) {
      return 'Uploaded';
    }

    return 'Missing';
  }

  get completionStatusText(): string {
    return this.processing?.isComplete ? 'Complete' : 'In Progress';
  }

  backToList(): void {
    this.router.navigate(['../'], { relativeTo: this.route });
  }

  loadPage(): void {
    this.loadingPage = true;
    this.errorMessage = '';

    this.loadTemplateWorkflows();
    this.loadAvailableUsers();
    this.loadProcessing();
  }

  loadProcessing(): void {
    this.dataService
      .getMigrationProcessing(this.migrationProcessingId)
      .subscribe({
        next: processing => {
          this.processing = processing;
          this.newOwnerId = processing.currentOwnerId || '';
          this.showDataEntitiesGrid = !processing.workflowMigratedOn;
          if (!this.workflowName.trim()) {
            this.workflowName = processing.sourceSystem || '';
          }
          this.loadWorkflowTaskStatus();
          this.loadNotes();
          this.loadingPage = false;

          // Validate application data if it exists
          this.validateExistingApplicationData();
        },
        error: err => {
          this.errorMessage =
            'Failed to load migration record: ' +
            (err.error?.message || err.message);
          this.loadingPage = false;
        }
      });
  }

  loadTemplateWorkflows(): void {
    this.workflowService.getTemplateLibraryWorkflows().subscribe({
      next: workflows => {
        this.templateWorkflows = workflows || [];
      },
      error: err => {
        console.error('Failed to load template workflows', err);
      }
    });
  }

  loadAvailableUsers(): void {
    this.dataService.getDoAnythingUsers().subscribe({
      next: users => {
        this.availableUsers = users || [];
      },
      error: err => {
        console.error('Failed to load owners', err);
        this.availableUsers = [];
      }
    });
  }

  loadNotes(): void {
    if (!this.processing?.id) {
      return;
    }

    this.dataService.getMigrationProcessingNotes(this.processing.id).subscribe({
      next: notes => {
        if (this.processing) {
          this.processing.migrationProcessingNotes = notes || [];
        }
      },
      error: err => {
        console.error('Failed to load notes', err);
        if (this.processing) {
          this.processing.migrationProcessingNotes = [];
        }
      }
    });
  }

  loadHistory(): void {
    if (!this.processing?.id) {
      return;
    }

    this.loadingHistory = true;
    forkJoin({
      executions: this.dataService.getMigrationExecutions(this.processing.id),
      applicationHistories: this.dataService.getMigrationApplicationHistories(
        this.processing.id
      )
    }).subscribe({
      next: ({ executions, applicationHistories }) => {
        this.migrationExecutions = executions || [];
        this.migrationApplicationHistories = applicationHistories || [];
        this.loadingHistory = false;
      },
      error: err => {
        console.error('Failed to load migration history', err);
        this.migrationExecutions = [];
        this.migrationApplicationHistories = [];
        this.loadingHistory = false;
      }
    });
  }

  resetSummary(): void {
    this.migrationSummary = null;
    this.hierarchicalActivities = [];
    this.summaryVerified = false;
    this.overwriteWorkflow = false;
    this.customListsToUpdate.clear();
    this.rolesToUpdate.clear();
    this.errorMessage = '';
    this.successMessage = '';
  }

  generateSummary(): void {
    if (!this.processing) {
      return;
    }

    if (!this.workflowName.trim()) {
      this.errorMessage =
        'Workflow name is required before generating a summary.';
      return;
    }

    this.working = true;
    this.errorMessage = '';
    this.successMessage = '';
    this.migrationSummary = null;
    this.customListsToUpdate.clear();
    this.rolesToUpdate.clear();

    const request = new WorkflowMigrationRequest({
      clientId: this.clientId,
      workflowName: this.workflowName.trim(),
      overwriteExistingWorkflow: this.overwriteWorkflow,
      templateWorkflowId: this.selectedWorkflowId || null,
      isVerified: false,
      migrationProcessingId: this.processing.id
    });

    this.dataService.migrateWorkflow(request).subscribe({
      next: (response: { verificationResult: MigrationSummary }) => {
        this.migrationSummary = response.verificationResult;
        this.hierarchicalActivities = buildHierarchicalActivities(
          this.migrationSummary
        );
        this.working = false;
      },
      error: err => {
        this.errorMessage =
          'Failed to generate migration summary: ' +
          (err.error?.message || err.message);
        this.working = false;
      }
    });
  }

  executeMigration(): void {
    if (!this.processing || !this.canExecuteMigration) {
      return;
    }

    this.working = true;
    this.errorMessage = '';
    this.successMessage = '';

    const request = new WorkflowMigrationRequest({
      clientId: this.clientId,
      workflowName: this.workflowName.trim(),
      overwriteExistingWorkflow: this.overwriteWorkflow,
      templateWorkflowId: this.selectedWorkflowId || null,
      isVerified: true,
      migrationProcessingId: this.processing.id,
      customListsToUpdate: Array.from(this.customListsToUpdate),
      rolesToUpdate: Array.from(this.rolesToUpdate)
    });

    this.dataService.migrateWorkflow(request).subscribe({
      next: (result: any) => {
        this.workflowTaskStatus = result?.status || 'Queued';
        this.successMessage = result?.scheduledTaskQueueId
          ? 'Workflow migration queued successfully.'
          : 'Workflow migration request submitted successfully.';
        this.resetSummary();
        this.showMigrationForm = false;
        this.showDataEntitiesGrid = false;
        this.working = false;
        this.loadProcessing();
      },
      error: err => {
        this.errorMessage =
          'Workflow migration failed: ' + (err.error?.message || err.message);
        this.working = false;
      }
    });
  }

  private loadWorkflowTaskStatus(): void {
    if (!this.processing?.id) {
      this.workflowTaskStatus = null;
      this.applicationCoordinatorTasks = [];
      return;
    }

    this.dataService
      .getMigrationScheduledTaskQueueItems(this.processing.id)
      .subscribe({
        next: queueItems => {
          this.setApplicationCoordinatorTasks(queueItems || []);
          const workflowTask = (queueItems || []).find(
            item => item.taskType === 'migrate-workflow'
          );

          if (!workflowTask) {
            this.workflowTaskStatus = null;
          } else if (workflowTask.errorMessage) {
            this.workflowTaskStatus = 'Error';
          } else if (workflowTask.completedOn) {
            this.workflowTaskStatus = 'Completed';
          } else if (workflowTask.executedOn) {
            this.workflowTaskStatus = 'Processing';
          } else {
            this.workflowTaskStatus = 'Queued';
          }
        },
        error: () => {
          this.workflowTaskStatus = null;
          this.applicationCoordinatorTasks = [];
        }
      });
  }

  requestMigrateApplicationData(): void {
    if (
      !this.processing?.id ||
      !this.processing.applicationDataFilePath ||
      !this.processing.migratedWorkflowId ||
      !this.applicationDataValidationStatus?.isValid
    ) {
      return;
    }

    this.working = true;
    this.applicationDataMigrationError = '';
    this.dataService
      .getMigrationScheduledTaskQueueItems(this.processing.id)
      .subscribe({
        next: queueItems => {
          this.setApplicationCoordinatorTasks(queueItems || []);
          this.working = false;
          if (this.activeApplicationCoordinatorTasks.length > 0) {
            this.queueAnotherApplicationMigrationConfirmModal.modalSubDescp =
              `${this.activeApplicationCoordinatorTasks.length} application migration coordinator task(s) are queued or executing. Queue another coordinator for the current application data file?`;
            this.queueAnotherApplicationMigrationConfirmModal.open = true;
            return;
          }

          this.migrateApplicationData();
        },
        error: err => {
          this.working = false;
          this.applicationDataMigrationError =
            err.error?.message ||
            err.message ||
            'Unable to verify whether an application migration is already queued. Refresh and try again.';
        }
      });
  }

  confirmQueueAnotherApplicationMigration(): void {
    this.queueAnotherApplicationMigrationConfirmModal.open = false;
    this.migrateApplicationData();
  }

  private setApplicationCoordinatorTasks(
    queueItems: ScheduledTaskQueueItem[]
  ): void {
    this.applicationCoordinatorTasks = queueItems
      .filter(item => item.taskType === 'migrate-workflow-applications-coordinator')
      .sort((left, right) => this.getQueuedTimestamp(right) - this.getQueuedTimestamp(left));
  }

  private getLatestTask(
    tasks: ScheduledTaskQueueItem[]
  ): ScheduledTaskQueueItem | null {
    return tasks.reduce<ScheduledTaskQueueItem | null>((latest, item) =>
      !latest || this.getQueuedTimestamp(item) > this.getQueuedTimestamp(latest)
        ? item
        : latest, null);
  }

  private getQueuedTimestamp(task: ScheduledTaskQueueItem): number {
    return task.queuedOn ? new Date(task.queuedOn).getTime() : 0;
  }

  private getScheduledTaskStatus(
    task: ScheduledTaskQueueItem
  ): 'Queued' | 'Processing' | 'Completed' | 'Cancelled' | 'Error' {
    if (task.errorMessage?.startsWith('Cancelled by')) {
      return 'Cancelled';
    }
    if (task.errorMessage) {
      return 'Error';
    }
    if (task.completedOn) {
      return 'Completed';
    }
    return task.executedOn ? 'Processing' : 'Queued';
  }

  addNote(): void {
    if (!this.processing?.id || !this.newNote.trim()) {
      return;
    }

    this.working = true;

    const note = new MigrationProcessingNote({
      migrationProcessingId: this.processing.id,
      note: this.newNote.trim()
    });

    this.dataService.saveMigrationProcessingNote(note).subscribe({
      next: createdNote => {
        if (!this.processing?.migrationProcessingNotes) {
          this.processing!.migrationProcessingNotes = [];
        }

        this.processing!.migrationProcessingNotes = [
          createdNote,
          ...this.processing!.migrationProcessingNotes
        ];
        this.newNote = '';
        this.working = false;
      },
      error: err => {
        this.errorMessage =
          'Failed to save note: ' + (err.error?.message || err.message);
        this.working = false;
      }
    });
  }

  saveOwner(): void {
    if (!this.processing?.id) {
      return;
    }

    this.working = true;

    this.dataService
      .changeMigrationProcessingOwner(this.processing.id, this.newOwnerId || '')
      .subscribe({
        next: updated => {
          this.processing = updated;
          this.newOwnerId = updated.currentOwnerId || '';
          this.showChangeOwnerDialog = false;
          this.successMessage = 'Migration owner updated.';
          this.working = false;
        },
        error: err => {
          this.errorMessage =
            'Failed to update owner: ' + (err.error?.message || err.message);
          this.working = false;
        }
      });
  }

  openCompleteModal(): void {
    this.completeConfirmModal.open = true;
  }

  openDeleteModal(): void {
    this.deleteConfirmModal.open = true;
  }

  markAsComplete(): void {
    if (!this.processing?.id) {
      return;
    }

    this.working = true;

    this.dataService.completeMigrationProcessing(this.processing.id).subscribe({
      next: () => {
        this.completeConfirmModal.open = false;
        this.successMessage = 'Migration marked as complete.';
        this.working = false;
        this.backToList();
      },
      error: err => {
        this.errorMessage =
          'Failed to mark migration as complete: ' +
          (err.error?.message || err.message);
        this.working = false;
      }
    });
  }

  deleteMigrationProcessing(): void {
    if (!this.processing?.id) {
      return;
    }

    this.working = true;

    this.dataService.deleteMigrationProcessing(this.processing.id).subscribe({
      next: () => {
        this.deleteConfirmModal.open = false;
        this.working = false;
        this.backToList();
      },
      error: err => {
        this.errorMessage =
          'Failed to delete migration record: ' +
          (err.error?.message || err.message);
        this.working = false;
      }
    });
  }

  onApplicationDataUploaded(response: UploadResponse): void {
    if (!this.processing) {
      return;
    }

    this.processing.applicationDataFilePath = response.path;
    this.working = true;

    this.dataService.saveMigrationProcessing(this.processing).subscribe({
      next: updated => {
        this.processing = updated;
        this.working = false;
        this.validateApplicationData(response.path);
      },
      error: err => {
        this.errorMessage =
          'Saved file, but failed to update migration record: ' +
          (err.error?.message || err.message);
        this.working = false;
      }
    });
  }

  onApplicationDataDeleted(): void {
    if (!this.processing) {
      return;
    }

    this.processing.applicationDataFilePath = null;
    this.applicationDataValidationStatus = null;
    this.showValidationDetails = false;
    this.working = true;

    this.dataService.saveMigrationProcessing(this.processing).subscribe({
      next: updated => {
        this.processing = updated;
        this.working = false;
      },
      error: err => {
        this.errorMessage =
          'Failed to clear application data file: ' +
          (err.error?.message || err.message);
        this.working = false;
      }
    });
  }

  validateExistingApplicationData(): void {
    if (this.processing?.applicationDataFilePath) {
      this.validateApplicationData(this.processing.applicationDataFilePath);
    }
  }

  validateApplicationData(filePath: string): void {
    const workflowId = this.processing?.migratedWorkflowId;
    if (!workflowId) {
      this.applicationDataValidationStatus = {
        isValid: false,
        isValidating: false,
        recordCount: 0,
        errors: [
          'Cannot validate application data until the workflow migration has been executed.'
        ],
        matchedProperties: [],
        unmatchedProperties: [],
        missingRequiredProperties: []
      };
      return;
    }

    this.applicationDataValidationStatus = {
      isValid: false,
      isValidating: true,
      recordCount: 0,
      errors: [],
      matchedProperties: [],
      unmatchedProperties: [],
      missingRequiredProperties: []
    };

    this.dataService
      .validateMigratedApplicationData(filePath, workflowId)
      .subscribe({
        next: result => {
          this.applicationDataValidationStatus = {
            isValid: result.isValid,
            isValidating: false,
            recordCount: result.recordCount,
            errors: result.errors || [],
            matchedProperties: result.matchedProperties || [],
            unmatchedProperties: result.unmatchedProperties || [],
            missingRequiredProperties: result.missingRequiredProperties || []
          };
        },
        error: err => {
          this.applicationDataValidationStatus = {
            isValid: false,
            isValidating: false,
            recordCount: 0,
            errors: [
              'Failed to validate application data: ' +
                (err.error?.message || err.message)
            ],
            matchedProperties: [],
            unmatchedProperties: [],
            missingRequiredProperties: []
          };
        }
      });
  }

  migrateApplicationData(): void {
    if (
      !this.processing?.applicationDataFilePath ||
      !this.processing?.migratedWorkflowId ||
      !this.applicationDataValidationStatus?.isValid
    ) {
      return;
    }

    this.working = true;
    this.applicationDataMigrationError = '';

    this.dataService.migrateApplicationData(this.processing.id).subscribe({
      next: result => {
        this.processing = result.migrationProcessing || this.processing;
        this.successMessage = result?.scheduledTaskQueueId
          ? 'Application data migration queued successfully.'
          : 'Application data migration request submitted successfully.';
        this.applicationDataMigrationError = '';
        this.working = false;
        this.loadWorkflowTaskStatus();
      },
      error: err => {
        let errorMsg = 'Unknown error occurred';

        // Try to extract the most detailed error message
        if (err.error) {
          if (typeof err.error === 'string') {
            errorMsg = err.error;
          } else if (err.error.message) {
            errorMsg = err.error.message;
          } else if (err.error.ExceptionMessage) {
            errorMsg = err.error.ExceptionMessage;
          } else if (err.error.Message) {
            errorMsg = err.error.Message;
          }
        } else if (err.message) {
          errorMsg = err.message;
        } else if (err.statusText) {
          errorMsg = err.statusText;
        }

        this.applicationDataMigrationError = errorMsg;
        this.working = false;
        console.error('Application data migration error:', err);
      }
    });
  }

  onMetadataFileUploaded(response: UploadResponse): void {
    if (!this.processing) {
      return;
    }

    this.processing.metaDataFilePath = response.path;
    this.working = true;
    this.reprocessingMetadata = true;
    this.metadataValidationErrors = [];
    this.errorMessage = '';
    this.successMessage = '';

    this.dataService.saveMigrationProcessing(this.processing).subscribe({
      next: updated => {
        this.processing = updated;
        this.dataService.parseMetadataFile(updated.id).subscribe({
          next: result => {
            const validation = normalizeMigrationMetadataValidationResult({
              errors: result.validationErrors || [],
              warnings: result.validationWarnings || []
            });
            this.metadataValidationErrors = validation.errors;
            if (this.metadataValidationErrors.length > 0) {
              this.errorMessage =
                'Metadata validation failed. Correct the errors listed below and re-upload the file.';
            } else {
              this.successMessage =
                'Metadata file updated and parsed successfully.';
            }
            this.working = false;
            this.reprocessingMetadata = false;
          },
          error: err => {
            this.errorMessage =
              'Metadata uploaded, but parsing failed: ' +
              (err.error?.message || err.message);
            this.working = false;
            this.reprocessingMetadata = false;
          }
        });
      },
      error: err => {
        this.errorMessage =
          'Failed to update metadata file: ' +
          (err.error?.message || err.message);
        this.working = false;
        this.reprocessingMetadata = false;
      }
    });
  }

  onMetadataFileDeleted(): void {
    if (!this.processing) {
      return;
    }

    this.processing.metaDataFilePath = null;
    this.working = true;

    this.dataService.saveMigrationProcessing(this.processing).subscribe({
      next: updated => {
        this.processing = updated;
        this.working = false;
      },
      error: err => {
        this.errorMessage =
          'Failed to remove metadata file: ' +
          (err.error?.message || err.message);
        this.working = false;
      }
    });
  }

  onDataEntitiesChanged(): void {
    this.successMessage = 'Field mapping updated.';
  }

  startMigrationFlow(): void {
    if (!this.processing) {
      return;
    }

    if (!this.workflowName.trim()) {
      this.workflowName = this.processing.sourceSystem || '';
    }

    this.showMigrationForm = true;
    this.errorMessage = '';
    this.successMessage = '';
  }

  cancelMigrationFlow(): void {
    this.showMigrationForm = false;
    this.resetSummary();
  }

  openDataEntitiesGrid(): void {
    this.showDataEntitiesGrid = true;
  }

  hideDataEntitiesGrid(): void {
    this.showDataEntitiesGrid = false;
  }

  openChangeOwnerDialog(): void {
    this.newOwnerId = this.processing?.currentOwnerId || '';
    this.showChangeOwnerDialog = true;
  }

  cancelChangeOwnerDialog(): void {
    this.newOwnerId = this.processing?.currentOwnerId || '';
    this.showChangeOwnerDialog = false;
  }

  viewWorkflow(): void {
    if (!this.processing?.migratedWorkflowId) {
      return;
    }

    const workflowUrl = this.router.serializeUrl(
      this.router.createUrlTree([
        '/admin/workflow-builder',
        this.processing.migratedWorkflowId
      ])
    );

    window.open(workflowUrl, '_blank');
  }

  openExecutionWorkflow(workflowId: string): void {
    if (!workflowId) {
      return;
    }

    const workflowUrl = this.router.serializeUrl(
      this.router.createUrlTree(['/admin/workflow-builder', workflowId])
    );

    window.open(workflowUrl, '_blank');
  }

  openMigratedApplication(workflowApplicationId: string): void {
    if (!workflowApplicationId) {
      return;
    }

    const applicationUrl = this.router.serializeUrl(
      this.router.createUrlTree([
        '/application/workflow-application',
        workflowApplicationId
      ])
    );

    window.open(applicationUrl, '_blank');
  }

  downloadExecutionMetadataFile(filePath: string): void {
    if (!filePath) {
      return;
    }

    const downloadUrl =
      Utilities.getRootURL() +
      '/home/GetAzureDocument?documentPath=' +
      escape(filePath) +
      '&download=true';
    window.open(downloadUrl, '_blank');
  }

  hasErrors(): boolean {
    return !!this.migrationSummary?.errorMessages?.length;
  }

  hasExistingCustomLists(): boolean {
    return (
      this.migrationSummary?.customLists?.some(item => item.alreadyExists) ||
      false
    );
  }

  hasExistingRoles(): boolean {
    return (
      this.migrationSummary?.roles?.some(item => item.alreadyExists) || false
    );
  }

  toggleCustomListUpdate(listName: string): void {
    if (this.customListsToUpdate.has(listName)) {
      this.customListsToUpdate.delete(listName);
      return;
    }

    this.customListsToUpdate.add(listName);
  }

  toggleRoleUpdate(roleName: string): void {
    if (this.rolesToUpdate.has(roleName)) {
      this.rolesToUpdate.delete(roleName);
      return;
    }

    this.rolesToUpdate.add(roleName);
  }

  isCustomListSelected(listName: string): boolean {
    return this.customListsToUpdate.has(listName);
  }

  isRoleSelected(roleName: string): boolean {
    return this.rolesToUpdate.has(roleName);
  }

  downloadMetadataFile(): void {
    if (!this.processing?.id) {
      return;
    }

    this.working = true;
    this.dataService
      .downloadMigrationMetadataFile(this.processing.id)
      .subscribe({
        next: filePath => {
          if (filePath) {
            const downloadUrl =
              Utilities.getRootURL() +
              '/home/GetAzureDocument?documentPath=' +
              escape(filePath) +
              '&download=true';
            window.open(downloadUrl, '_blank');
          }
          this.working = false;
        },
        error: err => {
          this.errorMessage =
            'Failed to download metadata file: ' +
            (err.error?.message || err.message);
          this.working = false;
        }
      });
  }

  downloadApplicationDataFile(): void {
    if (!this.processing?.id) {
      return;
    }

    this.working = true;
    this.dataService
      .downloadMigrationApplicationDataFile(this.processing.id)
      .subscribe({
        next: filePath => {
          if (filePath) {
            const downloadUrl =
              Utilities.getRootURL() +
              '/home/GetAzureDocument?documentPath=' +
              escape(filePath) +
              '&download=true';
            window.open(downloadUrl, '_blank');
          }
          this.working = false;
        },
        error: err => {
          this.errorMessage =
            'Failed to download application data file: ' +
            (err.error?.message || err.message);
          this.working = false;
        }
      });
  }
}
